import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function CoachSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/club-coach/setup-info?token=${token}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.valid) { setName(data.name || ""); setEmail(data.email || ""); }
      })
      .catch(() => setInfo({ valid: false }))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const body = { token, name: name.trim(), password };
      if (!info?.email) body.email = email.trim().toLowerCase();
      const { data } = await axios.post(`${API}/club-coach/setup`, body);
      localStorage.setItem("clubCoachToken", data.access_token);
      localStorage.setItem("clubCoachInfo", JSON.stringify(data.coach || {}));
      toast.success("Account ready — welcome!");
      navigate("/club-dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white/50"><Loader2 className="animate-spin mr-2" size={18} /> Checking your invite…</div>;
  }

  if (!token || !info?.valid) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
        <div className="text-center max-w-sm bg-[#141414] border border-white/10 rounded-2xl p-8" data-testid="coach-setup-invalid">
          <ShieldCheck size={30} className="mx-auto mb-4 text-white/30" />
          <h1 className="font-headings text-2xl uppercase tracking-wide mb-2">Invite not valid</h1>
          <p className="text-white/55 text-sm mb-6">This coach invite link is invalid or has already been used. Ask your Head Coach for a fresh link, or log in if you've already set up.</p>
          <button data-testid="coach-setup-login" onClick={() => navigate("/club-login")} className="bg-[#C41E24] hover:bg-[#A0161D] text-white font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm">Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 10%, rgba(196,30,36,0.14), transparent 45%)" }} />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#C41E24]/15 border border-[#C41E24]/40 mb-4"><ShieldCheck size={26} className="text-[#C41E24]" /></div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#C41E24] mb-2">Next O Soccer · Coach Invite</div>
          <h1 className="font-headings text-3xl uppercase tracking-wide leading-none">Set Up Your Access</h1>
          <p className="text-sm text-white/50 mt-3">
            {info.club_name}{info.team_name ? ` · ${info.team_name}` : " · Club-wide"}
          </p>
        </div>
        <form onSubmit={submit} data-testid="coach-setup-form" className="bg-[#141414] border border-white/10 rounded-2xl p-6 sm:p-8 space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Your Name</label>
            <input data-testid="coach-setup-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Coach name"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Email</label>
            <input data-testid="coach-setup-email" type="email" required value={email} disabled={!!info.email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@club.com"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white text-sm disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Create a Password</label>
            <input data-testid="coach-setup-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
          </div>
          {error && <div data-testid="coach-setup-error" className="text-sm text-[#C41E24] bg-[#C41E24]/10 border border-[#C41E24]/30 rounded-md px-3 py-2">{error}</div>}
          <button data-testid="coach-setup-submit" type="submit" disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-sm font-bold uppercase tracking-wider py-3 rounded-md transition-colors disabled:opacity-60">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} {submitting ? "Setting up…" : "Activate My Access"}
          </button>
        </form>
      </div>
    </div>
  );
}
